import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import { WORLD_PRIVACIES, WORLD_STATUSES, type AdminWorldListItem } from '@/api/types'
import {
  useFeatureWorld,
  worldCategoriesQuery,
  worldsListQuery,
  worldPrivacyLabel,
  worldStatusLabel,
  type WorldFilters,
} from '@/features/worlds/worlds.queries'
import { PERMISSIONS, usePermission } from '@/features/auth/permissions'
import { formatCompact, formatDate } from '@/lib/format'
import {
  ActionMenu,
  Badge,
  Card,
  DataTable,
  PageHeader,
  SearchInput,
  useToast,
  type Column,
} from '@/ui'
import { IconPin, IconStar } from '@/icons'

const searchSchema = z.object({
  q: z.string().default(''),
  status: z.enum(['all', ...WORLD_STATUSES]).default('all'),
  privacy: z.enum(['all', ...WORLD_PRIVACIES]).default('all'),
  categoryKey: z.string().optional(),
  featured: z.boolean().optional(),
  sort: z.enum(['createdAt', 'visits', 'rating', 'activeCount']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
})

export const Route = createFileRoute('/_authed/worlds/')({
  validateSearch: searchSchema,
  component: WorldsPage,
})

function WorldsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const routeNavigate = Route.useNavigate()
  const search = Route.useSearch()
  const toast = useToast()

  const canFeature = usePermission(PERMISSIONS.WORLD_FEATURE)
  const feature = useFeatureWorld()
  const categories = useQuery(worldCategoriesQuery)

  const filters: WorldFilters = {
    q: search.q,
    status: search.status,
    privacy: search.privacy,
    categoryKey: search.categoryKey,
    featured: search.featured,
    sort: search.sort,
    order: search.order,
  }

  const query = useInfiniteQuery(worldsListQuery(filters))
  const { items, total } = flattenPages<AdminWorldListItem>(query.data)

  function toggleFeatured(world: AdminWorldListItem) {
    feature.mutate(
      { slug: world.slug, isFeatured: !world.isFeatured },
      {
        onSuccess: () => {
          toast.success(t('worlds.featureSaved'))
        },
        onError: (error) => {
          toast.fromError(error, t('state.errorTitle'))
        },
      },
    )
  }

  const columns: Column<AdminWorldListItem>[] = [
    {
      id: 'world',
      header: t('worlds.columnWorld'),
      sortKey: 'createdAt',
      cell: (world) => (
        <div className="flex min-w-0 items-center gap-2.5">
          {world.thumbnailUrl ? (
            <img
              src={world.thumbnailUrl}
              alt=""
              width={40}
              height={24}
              loading="lazy"
              className="h-6 w-10 shrink-0 rounded object-cover"
            />
          ) : (
            <span aria-hidden className="h-6 w-10 shrink-0 rounded bg-surface-sunken" />
          )}
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 truncate font-medium text-text">
              {world.title}
              {world.isFeatured ? <IconPin size={12} className="shrink-0 text-primary" /> : null}
            </p>
            <p className="truncate text-xs text-text-secondary">{world.ownerDisplayName}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('worlds.columnStatus'),
      cell: (world) =>
        world.isDeleted ? (
          <Badge tone="danger" hasDot>
            {t('worlds.badgeDeleted')}
          </Badge>
        ) : (
          <Badge tone={world.status === 'PUBLISHED' ? 'success' : 'neutral'} hasDot>
            {((key) => (key ? t(key) : world.status))(worldStatusLabel(world.status))}
          </Badge>
        ),
    },
    {
      id: 'privacy',
      header: t('worlds.columnPrivacy'),
      isSecondary: true,
      cell: (world) => (
        <span className="text-text-secondary">
          {((key) => (key ? t(key) : world.privacy))(worldPrivacyLabel(world.privacy))}
        </span>
      ),
    },
    {
      id: 'active',
      header: t('worlds.columnActive'),
      align: 'end',
      cell: (world) => (
        <span data-numeric className={world.activeCount > 0 ? 'text-success' : 'text-text-muted'}>
          {world.activeCount}
        </span>
      ),
    },
    {
      id: 'visits',
      header: t('worlds.columnVisits'),
      sortKey: 'visits',
      align: 'end',
      cell: (world) => <span data-numeric>{formatCompact(world.visits, i18n.language)}</span>,
    },
    {
      id: 'rating',
      header: t('worlds.columnRating'),
      sortKey: 'rating',
      align: 'end',
      isSecondary: true,
      cell: (world) =>
        world.averageRating === null ? (
          <span className="text-text-muted">{t('common.notSet')}</span>
        ) : (
          <span data-numeric className="inline-flex items-center gap-1">
            <IconStar size={12} className="text-warning" />
            {world.averageRating.toFixed(1)}
          </span>
        ),
    },
    {
      id: 'created',
      header: t('worlds.columnCreated'),
      sortKey: 'createdAt',
      align: 'end',
      isSecondary: true,
      cell: (world) => (
        <span className="text-text-secondary">{formatDate(world.createdAt, i18n.language)}</span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'end',
      width: '3rem',
      cell: (world) => (
        <ActionMenu
          actions={
            canFeature
              ? [
                  {
                    id: 'feature',
                    label: world.isFeatured ? t('worlds.unfeature') : t('worlds.feature'),
                    icon: <IconPin size={15} />,
                    onSelect: () => {
                      toggleFeatured(world)
                    },
                  },
                ]
              : []
          }
        />
      ),
    },
  ]

  const hasFilters =
    Boolean(search.q) || search.status !== 'all' || search.privacy !== 'all' || search.featured

  return (
    <>
      <PageHeader
        title={t('worlds.title')}
        description={t('worlds.subtitle')}
        actions={
          total > 0 ? (
            <span data-numeric className="text-[0.8125rem] text-text-secondary">
              {t('common.showing', { count: items.length, total })}
            </span>
          ) : undefined
        }
      />

      <Card isFlush>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput
            value={search.q}
            onChange={(q) => {
              void routeNavigate({ search: (prev) => ({ ...prev, q }) })
            }}
            placeholder={t('worlds.searchPlaceholder')}
            className="min-w-56 flex-1"
          />

          <FilterSelect
            label={t('worlds.status')}
            value={search.status}
            options={[
              { value: 'all', label: t('common.all') },
              ...WORLD_STATUSES.map((status) => ({
                value: status,
                label: t(worldStatusLabel(status) ?? 'worlds.statusDRAFT'),
              })),
            ]}
            onChange={(value) => {
              void routeNavigate({
                search: (prev) => ({ ...prev, status: value as typeof search.status }),
              })
            }}
          />

          <FilterSelect
            label={t('worlds.privacy')}
            value={search.privacy}
            options={[
              { value: 'all', label: t('common.all') },
              ...WORLD_PRIVACIES.map((privacy) => ({
                value: privacy,
                label: t(worldPrivacyLabel(privacy) ?? 'worlds.privacyPUBLIC'),
              })),
            ]}
            onChange={(value) => {
              void routeNavigate({
                search: (prev) => ({ ...prev, privacy: value as typeof search.privacy }),
              })
            }}
          />

          <FilterSelect
            label={t('worlds.category')}
            value={search.categoryKey ?? ''}
            options={[
              { value: '', label: t('common.all') },
              ...(categories.data?.items ?? []).map((category) => ({
                value: category.key,
                label: category.name,
              })),
            ]}
            onChange={(value) => {
              void routeNavigate({
                search: (prev) => ({ ...prev, categoryKey: value || undefined }),
              })
            }}
          />
        </div>

        <DataTable
          columns={columns}
          rows={items}
          getRowId={(world) => world.slug}
          caption={t('worlds.title')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          sort={{ key: search.sort, order: search.order }}
          onSortChange={(next) => {
            void routeNavigate({
              search: (prev) => ({ ...prev, sort: next.key as typeof search.sort, order: next.order }),
            })
          }}
          onRowClick={(world) => {
            void navigate({ to: '/worlds/$slug', params: { slug: world.slug } })
          }}
          emptyTitle={hasFilters ? t('state.emptyFiltered') : t('worlds.empty')}
          emptyDescription={hasFilters ? t('state.emptyFilteredHint') : undefined}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => {
            void query.fetchNextPage()
          }}
        />
      </Card>
    </>
  )
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  return (
    <>
      <label className="sr-only" htmlFor={`filter-${label}`}>
        {label}
      </label>
      <select
        id={`filter-${label}`}
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </>
  )
}
